import React from 'react';
import { Card, CardBody, Button, Spinner } from 'reactstrap';
import {
  FaChartLine,
  FaChartPie,
  FaArrowTrendDown,
  FaUsers,
  FaChevronRight,
} from 'react-icons/fa6';
import InsightDetailModal from './InsightDetailModal';

/**
 * Panel lateral de insights de la última corrida.
 *
 * `insights = []` por defecto y los estados separados existen porque antes la
 * tarjeta mostraba "Aún no hay insights" en tres situaciones distintas: cuando
 * de verdad no hay, mientras cargaba, y cuando la petición fallaba. Las tres se
 * veían igual, así que un endpoint caído parecía simplemente falta de datos.
 */
const InsightsCard = ({ insights = [], isLoading = false, error = null }) => {
  const [selected, setSelected] = React.useState(null);
  // "Ver todos" no navega a otra pantalla: expande la lista acá mismo. Son
  // pocos insights por corrida, uno por tipo, así que una vista aparte sería
  // más ceremonia que valor.
  const [showAll, setShowAll] = React.useState(false);

  const VISIBLE = 4;
  const visibleInsights = showAll ? insights : insights.slice(0, VISIBLE);

  const getInsightIcon = (index) => {
    const icons = [
      {
        icon: <FaChartLine size={15} />,
        background: '#eef5ff',
        color: '#2563eb',
      },
      {
        icon: <FaChartPie size={15} />,
        background: '#eafaf0',
        color: '#16a34a',
      },
      {
        icon: <FaArrowTrendDown size={15} />,
        background: '#fff2e8',
        color: '#f97316',
      },
      {
        icon: <FaUsers size={15} />,
        background: '#f4efff',
        color: '#7c3aed',
      },
    ];

    return icons[index % icons.length];
  };

  return (
    <Card
      className="mb-3"
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e7ebf0',
        borderRadius: '8px',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)',
      }}
    >
      <CardBody
        style={{
          padding: '16px',
        }}
      >
        <h6
          style={{
            fontSize: '13px',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '12px',
          }}
        >
          Insights Automáticos
        </h6>

        {isLoading && (
          <div className="d-flex align-items-center gap-2" style={{ color: '#64748b', fontSize: '11px' }}>
            <Spinner size="sm" />
            Cargando insights…
          </div>
        )}

        {!isLoading && error && (
          <p
            style={{
              color: '#b91c1c',
              fontSize: '11px',
              marginBottom: '0',
            }}
          >
            No se pudieron cargar los insights: {error.message || 'error desconocido'}
          </p>
        )}

        {!isLoading && !error && !insights.length && (
          <p
            style={{
              color: '#64748b',
              fontSize: '11px',
              marginBottom: '0',
            }}
          >
            Aún no hay insights de la última corrida.
          </p>
        )}

        {!isLoading && !error && visibleInsights.map((insight, index) => {
          const icon = getInsightIcon(index);

          return (
            <div
              key={insight.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(insight)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelected(insight);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '67px',
                padding: '8px 10px',
                marginBottom: '7px',
                border: '1px solid #e7ebf0',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  minWidth: '36px',
                  borderRadius: '50%',
                  backgroundColor: icon.background,
                  color: icon.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                }}
              >
                {icon.icon}
              </div>

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    color: '#273142',
                    fontSize: '11px',
                    fontWeight: '600',
                    lineHeight: '1.4',
                    marginBottom: '2px',
                  }}
                >
                  "{insight.text}"
                </div>

                <div
                  style={{
                    color: '#64748b',
                    fontSize: '10px',
                    lineHeight: '1.4',
                  }}
                >
                  {insight.user}
                </div>
              </div>

              <FaChevronRight
                size={11}
                style={{
                  color: '#475569',
                  marginLeft: '10px',
                  flexShrink: 0,
                }}
              />
            </div>
          );
        })}

        {!isLoading && !error && insights.length > VISIBLE && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '5px',
            }}
          >
            <Button
              color="link"
              size="sm"
              className="p-0"
              onClick={() => setShowAll((value) => !value)}
              style={{
                color: '#2563eb',
                fontSize: '10px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {showAll
                ? 'Ver menos'
                : `Ver todos los insights (${insights.length})`}
              <FaChevronRight size={9} />
            </Button>
          </div>
        )}
      </CardBody>

      <InsightDetailModal
        isOpen={Boolean(selected)}
        toggle={() => setSelected(null)}
        insight={selected}
      />
    </Card>
  );
};

export default InsightsCard;